import { X, Sparkles, Info } from 'lucide-react'

export function GenerationModal({
  open,
  onClose,
  config,
  onConfigChange,
  models,
  selectedCount,
  costEstimate,
  generating,
  onGenerate,
}) {
  if (!open) return null

  const handleSubmit = (e) => {
    e.preventDefault()
    onGenerate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-orange-500" />
            Generate Images
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-6">
          {/* Selected items count */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 font-medium">
              {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected for generation
            </p>
          </div>

          {/* Model Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
            <select
              value={config.model}
              onChange={(e) => onConfigChange({ ...config, model: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {models.map((model) => (
                <option key={model.id} value={model.id} disabled={!model.available}>
                  {model.name} {model.available ? '' : '(requires access)'}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Sync: ~${models.find(m => m.id === config.model)?.sync_cost_per_image || 0.039}/img | 
              Async: ~${models.find(m => m.id === config.model)?.async_cost_per_image || 0.0195}/img
            </p>
          </div>

          {/* Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prompt</label>
            <textarea
              value={config.prompt}
              onChange={(e) => onConfigChange({ ...config, prompt: e.target.value })}
              rows={4}
              placeholder="Describe how you want the food images to look..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          {/* Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Generation Mode</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="sync"
                  checked={config.mode === 'sync'}
                  onChange={(e) => onConfigChange({ ...config, mode: e.target.value })}
                  className="text-orange-500 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Sync (Fast, ~2-5s/img)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="async"
                  checked={config.mode === 'async'}
                  onChange={(e) => onConfigChange({ ...config, mode: e.target.value })}
                  className="text-orange-500 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Async (50% cheaper, hours)</span>
              </label>
            </div>
          </div>

          {/* Aspect Ratio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
            <select
              value={config.aspect_ratio}
              onChange={(e) => onConfigChange({ ...config, aspect_ratio: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="1:1">1:1 (Square)</option>
              <option value="16:9">16:9 (Landscape)</option>
              <option value="9:16">9:16 (Portrait)</option>
              <option value="4:3">4:3</option>
              <option value="3:4">3:4</option>
            </select>
          </div>

          {/* Cost Estimate */}
          {costEstimate !== null && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5 text-green-600" />
                <span className="text-green-800">Estimated cost: <strong>${costEstimate.toFixed(4)}</strong> for {selectedCount} images</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={generating || !config.prompt.trim() || selectedCount === 0}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? 'Generating...' : 'Start Generation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}